"use client";

import { Download } from "lucide-react";
import { encodeOpus, encodeWav, type PcmAudio } from "mediatools";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { formatBytes } from "./format";

export interface ProcessedAudio {
  /** The processed (decoded + transformed) audio, not yet encoded. */
  pcm: PcmAudio;
  /** Output filename without extension. */
  baseName: string;
  /** Original input size, for an at-a-glance comparison. */
  inputBytes?: number;
}

interface EncodedFile {
  url: string;
  size: number;
}

function triggerDownload(url: string, fileName: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Shows the processed audio: an inline player plus one download button per
 * supported format. WAV is encoded eagerly (it's instant and powers the
 * preview); Opus is encoded lazily on first click (WebCodecs, async) and cached.
 *
 * Keeping encode separate from process means switching/adding a format never
 * re-runs the tool's work — it just re-encodes the same PCM.
 */
export function AudioResultPanel({
  result,
  opusSupported,
  title = "Done",
}: {
  result: ProcessedAudio;
  opusSupported: boolean;
  title?: string;
}) {
  const t = useTranslations("mediaTools.result.audio");
  const [wav, setWav] = useState<EncodedFile | null>(null);
  const [opus, setOpus] = useState<EncodedFile | null>(null);
  const [opusBusy, setOpusBusy] = useState(false);
  const [opusError, setOpusError] = useState<string | null>(null);

  // Encode WAV eagerly for the preview + instant download; reset on new result.
  useEffect(() => {
    const blob = new Blob([encodeWav(result.pcm, { bitDepth: 16 })], { type: "audio/wav" });
    const url = URL.createObjectURL(blob);
    setWav({ url, size: blob.size });
    setOpus(null);
    setOpusError(null);
    return () => URL.revokeObjectURL(url);
  }, [result]);

  // Revoke the lazily-created Opus URL when it's replaced or on unmount.
  useEffect(() => {
    if (!opus) return;
    return () => URL.revokeObjectURL(opus.url);
  }, [opus]);

  const downloadWav = () => {
    if (wav) triggerDownload(wav.url, `${result.baseName}.wav`);
  };

  const downloadOpus = async () => {
    if (opus) {
      triggerDownload(opus.url, `${result.baseName}.webm`);
      return;
    }
    setOpusBusy(true);
    setOpusError(null);
    try {
      const blob = new Blob([await encodeOpus(result.pcm)], { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      setOpus({ url, size: blob.size });
      triggerDownload(url, `${result.baseName}.webm`);
    } catch (cause) {
      setOpusError(cause instanceof Error ? cause.message : t("opusEncodingFailed"));
    } finally {
      setOpusBusy(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="mb-3 font-medium">{title}</p>

      {wav && <audio src={wav.url} controls className="mb-4 w-full" />}

      <div className="flex flex-wrap gap-3">
        <Button onClick={downloadWav} disabled={!wav}>
          <Download className="mr-2 h-4 w-4" />
          {t("downloadWav")}
          {wav ? ` · ${formatBytes(wav.size)}` : ""}
        </Button>
        {opusSupported && (
          <Button variant="secondary" onClick={downloadOpus} loading={opusBusy} disabled={opusBusy}>
            {opusBusy ? (
              <Spinner className="mr-2 h-4 w-4" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {t("downloadOpus")}
            {opus ? ` · ${formatBytes(opus.size)}` : ""}
          </Button>
        )}
      </div>

      <p className="mt-2 text-xs text-foreground/60">
        {result.inputBytes !== undefined
          ? `${t("originalSize", { size: formatBytes(result.inputBytes) })} `
          : ""}
        {t("formatHelp")}
      </p>

      {opusError && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {opusError}
        </p>
      )}
    </div>
  );
}
